import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Text,
  useToast,
} from '@chakra-ui/react';

import { apiGetActivePolicyReferral, apiRecallBaraReferral } from 'api';
import { useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { updateWipStatus as updateAutoWipStatus } from 'reducers/autoPersonalFormReducer';
import { updateWipStatus as updateHomeWipStatus } from 'reducers/homePersonalFormReducer';
import {
  BARA_REFERRAL_STATUS_READY_FOR_REVIEW,
  BARA_REFERRAL_STATUS_UNDER_REVIEW,
  BARA_REFERRAL_STATUS_APPROVED,
  BARA_REFERRAL_STATUS_DECLINED,
  BARA_REFERRAL_STATUS_NEEDS_INFO,
  WIP_STATUS_APPLICATION_CREATION,
  WIP_STATUS_BARA_SUBMITTED,
  WIP_STATUS_BARA_UNDER_REVIEW,
  WIP_STATUS_BARA_NEEDS_INFO,
  WIP_STATUS_BARA_APPROVED,
  WIP_STATUS_BARA_DECLINED,
} from 'variables/constants';
import ReferralReviewModal from 'views/bara/ReferralReviewModal';

interface ActiveReferral {
  id: string;
  status: number;
  status_display: string;
  jira_ticket_key: string;
  can_recall: boolean;
}

const STATUS_CONFIG: Record<
  number,
  { label: string; message: string; color: string; icon: string }
> = {
  [BARA_REFERRAL_STATUS_READY_FOR_REVIEW]: {
    label: 'Submitted — Awaiting Assignment',
    message:
      'Your referral has been submitted and is waiting for an MP/Delegate to review it.',
    color: 'yellow',
    icon: '🕐',
  },
  [BARA_REFERRAL_STATUS_UNDER_REVIEW]: {
    label: 'Under Review',
    message:
      'Your submission is being reviewed. You will be notified once a decision is made.',
    color: 'blue',
    icon: '🔎',
  },
  [BARA_REFERRAL_STATUS_NEEDS_INFO]: {
    label: 'Info Requested',
    message:
      'Your reviewer has requested more information. Check the comments and respond.',
    color: 'orange',
    icon: '⚠️',
  },
  [BARA_REFERRAL_STATUS_APPROVED]: {
    label: 'Approved',
    message:
      'Your after-hours referral has been approved. The submission proceeds through normal workflow.',
    color: 'green',
    icon: '✅',
  },
  [BARA_REFERRAL_STATUS_DECLINED]: {
    label: 'Declined',
    message:
      'Your after-hours referral has been declined. Please contact your Managing Partner.',
    color: 'red',
    icon: '❌',
  },
};

const REFERRAL_TO_WIP_STATUS: Record<number, number> = {
  [BARA_REFERRAL_STATUS_READY_FOR_REVIEW]: WIP_STATUS_BARA_SUBMITTED,
  [BARA_REFERRAL_STATUS_UNDER_REVIEW]: WIP_STATUS_BARA_UNDER_REVIEW,
  [BARA_REFERRAL_STATUS_NEEDS_INFO]: WIP_STATUS_BARA_NEEDS_INFO,
  [BARA_REFERRAL_STATUS_APPROVED]: WIP_STATUS_BARA_APPROVED,
  [BARA_REFERRAL_STATUS_DECLINED]: WIP_STATUS_BARA_DECLINED,
};

interface Props {
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
}

const BaraBrokerStatusBanner = ({ formStateKey }: Props) => {
  const { uuid } = useParams<{ uuid: string }>();
  const dispatch = useAppDispatch();
  const isBaraFixesEnabled = useFeatureFlag('enable-bara-referral-fixes');
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [referral, setReferral] = useState<ActiveReferral | null>(null);
  const [recalling, setRecalling] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showRecalledMessage, setShowRecalledMessage] = useState(false);

  const fetchReferral = useCallback(async () => {
    if (!uuid) return;
    try {
      const data: any = await apiGetActivePolicyReferral(uuid);
      const r = data?.referral;
      // Show for all active BARA statuses the broker cares about
      if (r && r.status in STATUS_CONFIG) {
        setReferral(r);
        if (!isBaraFixesEnabled) return;
        const action =
          formStateKey === 'autoPersonalForm'
            ? updateAutoWipStatus
            : updateHomeWipStatus;
        dispatch((dispatch, getState) => {
          const wipStatus = getState()[formStateKey].wip_status;
          // Preserve later binder/signing statuses, including changes during the request.
          if (
            wipStatus === WIP_STATUS_APPLICATION_CREATION ||
            Object.values(REFERRAL_TO_WIP_STATUS).includes(wipStatus)
          ) {
            dispatch(action(REFERRAL_TO_WIP_STATUS[r.status]));
          }
        });
      } else {
        setReferral(null);
      }
    } catch {
      setReferral(null);
    }
  }, [uuid, formStateKey, dispatch, isBaraFixesEnabled]);

  useEffect(() => {
    fetchReferral();
    if (!isBaraFixesEnabled) return;
    const interval = window.setInterval(fetchReferral, 30000);
    return () => window.clearInterval(interval);
  }, [fetchReferral, isBaraFixesEnabled]);

  const handleRecall = async () => {
    if (!referral) return;
    setRecalling(true);
    try {
      await apiRecallBaraReferral(referral.id);
      setReferral(null);
      setShowRecalledMessage(true);
      if (isBaraFixesEnabled) {
        const action =
          formStateKey === 'autoPersonalForm'
            ? updateAutoWipStatus
            : updateHomeWipStatus;
        dispatch(action(WIP_STATUS_APPLICATION_CREATION));
      }
    } catch (err: any) {
      toastRef.current({
        title: err?.detail || 'Failed to recall referral',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setRecalling(false);
    }
  };

  if (!referral) {
    if (!showRecalledMessage) return null;
    return (
      <Box
        mb="20px"
        px={4}
        py={3}
        bg="green.50"
        border="1px solid"
        borderColor="green.300"
        borderRadius="md"
        fontSize="sm"
        color="green.800"
      >
        ✅ Your referral has been recalled. You can continue making edits to the
        submission. You can create a MP/Delegate Referral after you're satisfied
        with your changes or route it to Portfolio Underwriter.
      </Box>
    );
  }

  const config = STATUS_CONFIG[referral.status];
  if (!config) return null;

  return (
    <>
      <Box
        mb="20px"
        bg={`${config.color}.50`}
        border="1px solid"
        borderColor={`${config.color}.200`}
        borderRadius="md"
        px={4}
        py={3}
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Text fontSize="sm">{config.icon}</Text>
            <Box>
              <HStack spacing={2} mb={0.5}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                  Referral submitted to MP/Delegate
                </Text>
                <Badge
                  colorScheme={config.color}
                  borderRadius="full"
                  fontSize="10px"
                  px={2}
                >
                  {config.label}
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.500">
                {config.message}
              </Text>
            </Box>
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={() => setModalOpen(true)}
            >
              View Comments
            </Button>
            {referral.can_recall && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="gray"
                isLoading={recalling}
                onClick={handleRecall}
              >
                Recall
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      <ReferralReviewModal
        referralId={referral.id}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default BaraBrokerStatusBanner;
